import * as THREE from 'three';

/**
 * On-screen counters for stress testing.
 *
 * Frame rate has to be read while the camera is moving, which is exactly when
 * the browser's own profiler is least convenient, so it goes on the page. The
 * static figures — chunk and triangle totals — are set once; only the live ones
 * are rewritten, and at four times a second rather than every frame, because
 * touching the DOM per frame would itself distort what is being measured.
 *
 * Starts hidden: it is a debug tool and debug mode is off until asked for.
 * Frames keep being counted while it is out of sight, so the rate shown on the
 * way back in is the rate the game was actually running at, not one measured
 * from scratch.
 */

const REFRESH_MS = 250;

interface StaticCounts {
  chunks: number;
  cells: number;
  triangles: number;
}

/**
 * A cell the HUD names: where the player stands, and where the cursor points.
 *
 * Elevation and biome are optional because the ground is not always in memory:
 * after a move the position is known before the chunk it points at has arrived,
 * and saying "—" is honest where inventing a level would not be.
 */
export interface Cell {
  q: number;
  r: number;
  elevation: number | null;
  biome: string | null;
}

function describe(label: string, cell: Cell | null): string {
  if (!cell) {
    // The line stays, so the block does not change height as the cursor leaves
    // the map — a HUD that jumps is a HUD that is hard to read a number off.
    return `${label} —`;
  }

  const height = cell.elevation === null ? '—' : String(cell.elevation);
  return `${label} ${cell.q}, ${cell.r} · wys ${height} · ${cell.biome ?? '—'}`;
}

function summarise(counts: StaticCounts): string {
  return (
    `${counts.chunks} chunks · ` +
    `${counts.cells.toLocaleString('en-US')} cells · ` +
    `${counts.triangles.toLocaleString('en-US')} tris`
  );
}

export class StatsHud {
  private sky = '';
  private view = '';
  private readonly element: HTMLElement;
  private summary: string;

  private frames = 0;
  private windowStart = performance.now();
  private lastRefresh = 0;
  private fps = 0;
  private pickMs = 0;
  private shown = false;
  private standing: Cell | null = null;
  private hovered: Cell | null = null;
  /** Whether there is a player at all: before joining, neither line applies. */
  private inWorld = false;

  constructor(counts: StaticCounts) {
    this.summary = summarise(counts);

    this.element = document.createElement('div');
    this.element.style.cssText = [
      'position:fixed',
      'top:12px',
      'left:12px',
      'padding:8px 12px',
      'background:rgba(0,0,0,0.72)',
      'color:#d1d5db',
      'font:12px/1.5 ui-monospace,SFMono-Regular,Menlo,monospace',
      'border-radius:6px',
      'border:1px solid rgba(255,255,255,0.12)',
      'white-space:pre',
      'pointer-events:none',
      'z-index:1000',
    ].join(';');

    this.element.style.display = 'none';
    document.body.appendChild(this.element);
    this.render(0, 0);
  }

  setVisible(visible: boolean): void {
    this.shown = visible;
    this.element.style.display = visible ? '' : 'none';
  }

  /** Call once per rendered frame, after renderer.render(). */
  /**
   * What is loaded, which now changes as the camera moves. It used to be fixed
   * at startup, back when the client held the whole world at once.
   */
  setCounts(counts: StaticCounts): void {
    this.summary = summarise(counts);
  }

  /**
   * Where the player is, and what the cursor is over. Set every frame and drawn
   * four times a second, like everything else here — a cheap map lookup on this
   * side is worth more than a rule about which of the places a position can
   * change from has to remember to say so.
   */
  setCells(standing: Cell | null, hovered: Cell | null): void {
    this.standing = standing;
    this.hovered = hovered;
    this.inWorld = this.inWorld || standing !== null;
  }

  /**
   * The world's clock and where the sun is, for checking that the day is doing
   * what it says. Without it "is it getting dark or is that the fog" is not a
   * question anybody can answer by looking.
   */
  setSky(clock: string, altitude: number, night: boolean, frozen = false): void {
    // The clock keeps running even when the light does not: debug mode holds
    // the sun still so measurements repeat, and the time of day is exactly the
    // thing that then becomes impossible to read off the screen.
    this.sky =
      `${night ? '🌙' : '☀️'} ${clock}  ${altitude.toFixed(0)}° nad horyzontem` +
      (frozen ? '  · światło stoi' : '');
  }

  /**
   * How close the camera is, and how much of the map that is.
   *
   * The zoom on its own is a number nobody has a feel for; next to it is what
   * it means — how many world units the frame covers across the ground, which
   * is the same measure the streaming radius and the fog are set from.
   */
  setView(zoom: number, reach: number): void {
    this.view = `🔍 zoom ${zoom.toFixed(2)}  ·  kadr ${(reach * 2).toFixed(0)} jednostek świata`;
  }

  update(renderer: THREE.WebGLRenderer, pickMs = 0): void {
    this.pickMs = pickMs;
    this.frames += 1;
    const now = performance.now();

    const elapsed = now - this.windowStart;
    if (elapsed >= REFRESH_MS) {
      this.fps = (this.frames * 1000) / elapsed;
      this.frames = 0;
      this.windowStart = now;
    }

    // Counting continues while hidden, writing does not — an off-screen HUD
    // has no business touching the DOM four times a second
    if (!this.shown) return;
    if (now - this.lastRefresh < REFRESH_MS) return;
    this.lastRefresh = now;

    this.render(renderer.info.render.calls, renderer.info.render.triangles);
  }

  private render(drawCalls: number, drawnTriangles: number): void {
    // Chrome only; absent elsewhere, in which case the line is dropped
    const memory = (performance as unknown as { memory?: { usedJSHeapSize: number } })
      .memory;

    const lines = [
      `${this.fps.toFixed(0).padStart(3)} fps   ${(1000 / (this.fps || 1)).toFixed(1)} ms`,
      ...(this.inWorld
        ? [describe('gracz ', this.standing), describe('kursor', this.hovered)]
        : []),
      this.summary,
      ...(this.sky ? [this.sky] : []),
      ...(this.view ? [this.view] : []),
      `${drawCalls} draw calls · ${drawnTriangles.toLocaleString('en-US')} tris drawn`,
      `pick ${this.pickMs.toFixed(2)} ms`,
    ];

    if (memory) {
      lines.push(`heap ${(memory.usedJSHeapSize / 1048576).toFixed(0)} MB`);
    }

    this.element.textContent = lines.join('\n');
  }

  dispose(): void {
    this.element.remove();
  }
}
